import { useMemo, useState } from "preact/hooks"
import { Forward, Pencil } from "lucide-react"
import { ActiveFilterIcon } from "@/global_filter"
import { Button } from "../../../../components/ui/button"
import { SearchableSelect } from "../../../../components/searchable_select"
import { exactCurrencyFormatter } from "../../../../utils"
import type { CalendarPromotionLoadState } from "../../promo_creation/hooks/use_promotional_context_api"
import { CalendarInfo, Promotion, PromotionStage } from "../../promo_creation/types"
import { ALL_PROMOTION_STAGES, stage_options } from "../stage_permissions"
import { use_org_users, user_name_by_id } from "../../promo_creation/hooks/use_org_users"
import { UserState } from "@/auth/user"

const PROMOTION_STAGES: PromotionStage[] = ALL_PROMOTION_STAGES
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

interface PromotionPlanningBoardProps {
  calendars: CalendarInfo[]
  onEditPromotion: (promotion: Promotion) => void
  onUpdateStage: (promotionId: string, stage: PromotionStage) => Promise<boolean>
  onDuplicatePromotion: (
    promotionId: string,
    options: {
      repeat_every_months?: number
      number_of_repetitions?: number
      custom_months?: Array<{ year: number; month: number }>
      copy_files?: boolean
    },
  ) => Promise<boolean>
  onCopyForward: (options: {
    source_year?: number
    target_year: number
    calendar_id?: string
    customer?: string
    retailer?: string
    planning_group?: string
    product?: string
    promotion_ids?: string[]
    copy_files?: boolean
  }) => Promise<Promotion[] | null>
  onSplitFixedFee: (promotionId: string) => Promise<boolean>
  promotion_load_by_calendar: Record<string, CalendarPromotionLoadState>
}

interface PromotionBoardCardProps {
  promotion: Promotion
  onEditPromotion: (promotion: Promotion) => void
  onUpdateStage: (promotionId: string, stage: PromotionStage) => Promise<boolean>
  onDuplicatePromotion: PromotionPlanningBoardProps["onDuplicatePromotion"]
  onSplitFixedFee: (promotionId: string) => Promise<boolean>
}

interface BoardGlobalFilters {
  year: string
  fundType: string
  customer: string
  retailer: string
  product: string
}

export function stageForPromotion(promotion: Promotion): PromotionStage {
  return promotion.stage || "Pending"
}

function formatDateRange(promotion: Promotion): string {
  if (promotion.start_date && promotion.end_date) return `${promotion.start_date} to ${promotion.end_date}`
  if (promotion.start_date) return `Starts ${promotion.start_date}`
  if (promotion.end_date) return `Ends ${promotion.end_date}`
  return "No dates"
}

export function buildCustomMonths(selectedMonths: Set<string>): Array<{ year: number; month: number }> {
  return Array.from(selectedMonths).map(value => {
    const [year, month] = value.split("-").map(Number)
    return { year, month }
  })
}

export function promotionMatchesBoardSearch(promotion: Promotion, searchValue: string): boolean {
  const tokens = searchValue.toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return true

  const searchText = [
    promotion.name,
    promotion.remote_promo_id,
    promotion.customer,
    promotion.retailer,
    promotion.planning_group,
    promotion.account_group,
    promotion.promotion_type,
    promotion.start_date,
    promotion.end_date,
    promotion.products.join(" "),
  ].filter(Boolean).join(" ").toLowerCase()

  return tokens.every(token => searchText.includes(token))
}

function promotionYears(promotion: Promotion): string[] {
  return [
    promotion.fiscal_year,
    promotion.start_date ? new Date(`${promotion.start_date}T12:00:00`).getFullYear() : undefined,
    promotion.end_date ? new Date(`${promotion.end_date}T12:00:00`).getFullYear() : undefined,
  ]
    .filter((year): year is number => Boolean(year && !Number.isNaN(year)))
    .map(String)
}

export function promotionMatchesGlobalFilters(promotion: Promotion, filters: BoardGlobalFilters): boolean {
  const yearMatches = !filters.year || promotionYears(promotion).includes(filters.year)
  const fundTypeMatches = !filters.fundType || promotion.promotion_type === filters.fundType
  const customerMatches = !filters.customer || promotion.customer === filters.customer
  const retailerMatches = !filters.retailer || promotion.retailer === filters.retailer
  const productMatches = !filters.product || promotion.products.includes(filters.product)

  return yearMatches && fundTypeMatches && customerMatches && retailerMatches && productMatches
}

function uniqueSorted(values: Array<string | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort()
}

interface BoardFilterSelectProps {
  label: string
  value: string
  onValueChange: (value: string) => void
  options: string[]
  allLabel: string
}

function BoardFilterSelect({ label, value, onValueChange, options, allLabel }: BoardFilterSelectProps) {
  return (
    <label class="space-y-1 text-xs font-medium text-gray-600">
      <span class="inline-flex items-center">
        {label}
        {value && <ActiveFilterIcon />}
      </span>
      <SearchableSelect
        value={value}
        onValueChange={onValueChange}
        options={[
          { value: "", label: allLabel },
          ...options.map(option => ({ value: option, label: option })),
        ]}
        placeholder={allLabel}
        searchPlaceholder={`Search ${label.toLowerCase()}...`}
        emptyMessage={`No matching ${label.toLowerCase()}.`}
        triggerClassName="h-9 text-sm"
      />
    </label>
  )
}

function PromotionBoardCard({
  promotion,
  onEditPromotion,
  onUpdateStage,
  onDuplicatePromotion,
  onSplitFixedFee,
}: PromotionBoardCardProps) {
  const [showDuplicate, setShowDuplicate] = useState(false)
  const [repeatEveryMonths, setRepeatEveryMonths] = useState(1)
  const [numberOfRepetitions, setNumberOfRepetitions] = useState(1)
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(new Set())
  const [copyFiles, setCopyFiles] = useState(false)
  const user = UserState.use()
  const users = use_org_users()
  const owner_name = user_name_by_id(users, promotion.owner_id)
  const approver_name = user_name_by_id(users, promotion.approver_id)
  const stage = stageForPromotion(promotion)
  // Brokers only see the transitions their role allows (submit/withdraw — never approve).
  const stageOptions = stage_options(user?.role, stage)
  const years = useMemo(() => {
    const baseYear = promotion.fiscal_year || new Date().getFullYear()
    return [baseYear, baseYear + 1, baseYear + 2, baseYear + 3]
  }, [promotion.fiscal_year])

  async function handleDuplicate() {
    if (!promotion.id) return
    const customMonths = buildCustomMonths(selectedMonths)
    await onDuplicatePromotion(promotion.id, {
      repeat_every_months: customMonths.length > 0 ? undefined : repeatEveryMonths,
      number_of_repetitions: customMonths.length > 0 ? undefined : numberOfRepetitions,
      custom_months: customMonths.length > 0 ? customMonths : undefined,
      copy_files: copyFiles,
    })
    setShowDuplicate(false)
  }

  function toggleMonth(year: number, month: number) {
    const key = `${year}-${month}`
    const nextMonths = new Set(selectedMonths)
    nextMonths.has(key) ? nextMonths.delete(key) : nextMonths.add(key)
    setSelectedMonths(nextMonths)
  }

  return (
    <div class="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div class="flex items-start justify-between gap-2">
        <div>
          <h4 class="text-sm font-semibold text-gray-900">{promotion.name}</h4>
          <p class="text-xs text-gray-500">{promotion.customer || "No customer"} · {promotion.retailer || "No retailer"}</p>
        </div>
        {promotion.file_count ? <span class="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">{promotion.file_count} files</span> : null}
      </div>
      <div class="mt-2 space-y-1 text-xs text-gray-600">
        <p>{promotion.promotion_type || "Promotion"} · {formatDateRange(promotion)}</p>
        {promotion.planned_amount ? <p>{exactCurrencyFormatter(promotion.planned_amount)} planned</p> : null}
        {promotion.planning_group ? <p>Planning group: {promotion.planning_group}</p> : null}
        {owner_name || approver_name ? (
          <p>
            {owner_name ? `Owner: ${owner_name}` : ""}
            {owner_name && approver_name ? " · " : ""}
            {approver_name ? `${stage === "Submitted" ? "Awaiting" : "Approver"}: ${approver_name}` : ""}
          </p>
        ) : null}
        {promotion.period_allocations && promotion.period_allocations.length > 0 ? (
          <p>{promotion.period_allocations.length} accrual periods</p>
        ) : null}
      </div>
      <div class="mt-3 flex items-center gap-1.5">
        <Button
          size="icon"
          variant="outline"
          class="h-8 w-8 shrink-0"
          onClick={() => onEditPromotion(promotion)}
          title="Edit promotion"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="icon"
          variant="outline"
          class="h-8 w-8 shrink-0"
          onClick={() => setShowDuplicate(!showDuplicate)}
          title="Copy promotion forward"
        >
          <Forward className="h-3.5 w-3.5" />
        </Button>
        {promotion.id ? (
          <select
            class="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-xs"
            value={stage}
            onChange={(event) => onUpdateStage(promotion.id!, (event.target as HTMLSelectElement).value as PromotionStage)}
          >
            {stageOptions.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
        ) : null}
        {promotion.id && promotion.planned_amount ? (
          <Button size="sm" variant="outline" class="shrink-0 px-2 text-xs" onClick={() => onSplitFixedFee(promotion.id!)}>Split</Button>
        ) : null}
      </div>
      {showDuplicate ? (
        <div class="mt-3 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
          <div class="grid grid-cols-2 gap-2">
            <label class="text-xs text-gray-600">
              Repeat every months
              <input class="mt-1 w-full rounded border border-gray-300 px-2 py-1" type="number" min="1" value={repeatEveryMonths} onInput={(event) => setRepeatEveryMonths(Number((event.target as HTMLInputElement).value))} />
            </label>
            <label class="text-xs text-gray-600">
              Repetitions
              <input class="mt-1 w-full rounded border border-gray-300 px-2 py-1" type="number" min="1" value={numberOfRepetitions} onInput={(event) => setNumberOfRepetitions(Number((event.target as HTMLInputElement).value))} />
            </label>
          </div>
          <label class="flex items-center gap-2 text-xs text-gray-600">
            <input type="checkbox" checked={copyFiles} onChange={(event) => setCopyFiles((event.target as HTMLInputElement).checked)} />
            Copy attached files
          </label>
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead>
                <tr>
                  <th class="p-1 text-left text-gray-500">Year</th>
                  {MONTHS.map(month => <th class="p-1 text-gray-500" key={month}>{month}</th>)}
                </tr>
              </thead>
              <tbody>
                {years.map(year => (
                  <tr key={year}>
                    <td class="p-1 font-medium text-gray-600">{year}</td>
                    {MONTHS.map((_, index) => (
                      <td class="p-1 text-center" key={`${year}-${index}`}>
                        <input
                          type="checkbox"
                          checked={selectedMonths.has(`${year}-${index + 1}`)}
                          onChange={() => toggleMonth(year, index + 1)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button size="sm" onClick={handleDuplicate}>Create Duplicates</Button>
        </div>
      ) : null}
    </div>
  )
}

export function PromotionPlanningBoard(props: PromotionPlanningBoardProps) {
  const [globalFilters, setGlobalFilters] = useState<BoardGlobalFilters>({
    year: "",
    fundType: "",
    customer: "",
    retailer: "",
    product: "",
  })
  const [stageSearchValues, setStageSearchValues] = useState<Record<PromotionStage, string>>({
    Pending: "",
    Submitted: "",
    Approved: "",
    Running: "",
    Completed: "",
    Cancelled: "",
  })
  const promotions = useMemo(() => props.calendars.flatMap(calendar => calendar.promotions), [props.calendars])
  const filterOptions = useMemo(() => {
    return {
      years: uniqueSorted(promotions.flatMap(promotionYears)),
      fundTypes: uniqueSorted(promotions.map(promotion => promotion.promotion_type)),
      customers: uniqueSorted(promotions.map(promotion => promotion.customer)),
      retailers: uniqueSorted(promotions.map(promotion => promotion.retailer)),
      products: uniqueSorted(promotions.flatMap(promotion => promotion.products)),
    }
  }, [promotions])
  const globallyFilteredPromotions = useMemo(() => {
    return promotions.filter(promotion => promotionMatchesGlobalFilters(promotion, globalFilters))
  }, [promotions, globalFilters])
  const promotionsByStage = useMemo(() => {
    return PROMOTION_STAGES.reduce<Record<PromotionStage, Promotion[]>>((groups, stage) => {
      const stageSearchValue = stageSearchValues[stage]
      groups[stage] = globallyFilteredPromotions.filter(promotion =>
        stageForPromotion(promotion) === stage && promotionMatchesBoardSearch(promotion, stageSearchValue)
      )
      return groups
    }, {} as Record<PromotionStage, Promotion[]>)
  }, [globallyFilteredPromotions, stageSearchValues])

  const board_hydrating_initial = props.calendars.some(
    (calendar_row) => props.promotion_load_by_calendar[calendar_row.id]?.loading_initial,
  )

  return (
    <div class={`space-y-4 transition-opacity ${board_hydrating_initial ? "opacity-85" : ""}`}>
      <div class="rounded-lg border border-gray-200 bg-white p-3">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-5">
          <BoardFilterSelect label="Year" value={globalFilters.year} onValueChange={(year) => setGlobalFilters({ ...globalFilters, year })} options={filterOptions.years} allLabel="All years" />
          <BoardFilterSelect label="Fund Type" value={globalFilters.fundType} onValueChange={(fundType) => setGlobalFilters({ ...globalFilters, fundType })} options={filterOptions.fundTypes} allLabel="All fund types" />
          <BoardFilterSelect label="Customer" value={globalFilters.customer} onValueChange={(customer) => setGlobalFilters({ ...globalFilters, customer })} options={filterOptions.customers} allLabel="All customers" />
          <BoardFilterSelect label="Retailer" value={globalFilters.retailer} onValueChange={(retailer) => setGlobalFilters({ ...globalFilters, retailer })} options={filterOptions.retailers} allLabel="All retailers" />
          <BoardFilterSelect label="Product" value={globalFilters.product} onValueChange={(product) => setGlobalFilters({ ...globalFilters, product })} options={filterOptions.products} allLabel="All products" />
        </div>
      </div>
      <div class="h-[720px] w-full overflow-auto rounded-lg border border-gray-200 bg-white">
        <div class="grid w-max grid-cols-[repeat(6,20rem)] gap-4 p-4">
        {PROMOTION_STAGES.map(stage => (
          <section class="h-[680px] overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-3" key={stage}>
            <div class="mb-3 flex items-center justify-between">
              <h3 class="text-sm font-semibold text-gray-900">{stage}</h3>
              <span class="rounded-full bg-white px-2 py-0.5 text-xs text-gray-500">{promotionsByStage[stage].length}</span>
            </div>
            <input
              class="mb-3 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={stageSearchValues[stage]}
              onInput={(event) => setStageSearchValues({
                ...stageSearchValues,
                [stage]: (event.target as HTMLInputElement).value,
              })}
              placeholder={`Search ${stage.toLowerCase()}...`}
            />
            <div class="space-y-3">
              {promotionsByStage[stage].map(promotion => (
                <PromotionBoardCard
                  key={promotion.id || promotion.name}
                  promotion={promotion}
                  onEditPromotion={props.onEditPromotion}
                  onUpdateStage={props.onUpdateStage}
                  onDuplicatePromotion={props.onDuplicatePromotion}
                  onSplitFixedFee={props.onSplitFixedFee}
                />
              ))}
              {promotionsByStage[stage].length === 0 ? (
                <p class="rounded-lg border border-dashed border-gray-300 p-4 text-center text-xs text-gray-400">No promotions</p>
              ) : null}
            </div>
          </section>
        ))}
        </div>
      </div>
    </div>
  )
}
